import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Download, ChevronLeft, ChevronRight, Loader2, Newspaper, FileText } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import TopMarquee from "@/components/site/TopMarquee";
import SmoothScroll from "@/components/site/SmoothScroll";

function fmt(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("hi-IN", { day: "numeric", month: "long", year: "numeric", weekday: "long" });
  } catch { return dateStr; }
}

export default function EPaperPage() {
  const [list, setList] = useState(null);
  const [current, setCurrent] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "ई-पेपर | देनवा पोस्ट";
    api.get("/public/epaper")
      .then((r) => {
        setList(r.data || []);
        if (r.data && r.data.length) setCurrent(r.data[0]);
      })
      .catch(() => setList([]));
  }, []);

  const pickEdition = (d) => { setCurrent(d); setPage(0); window.scrollTo({ top: 200, behavior: "smooth" }); };

  const files = current?.files || [];
  const pdfFile = files.find((f) => f.kind === "pdf");
  const imageFiles = files.filter((f) => f.kind === "image");

  return (
    <SmoothScroll>
      <div className="App font-sans-dev" data-testid="epaper-page">
        <TopMarquee />
        <Header />

        <main className="bg-[#F4F4F0]">
          {/* Hero */}
          <section className="border-b border-[#0F0F0F] px-4 md:px-8 py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto">
              <Link to="/" className="text-[12px] uppercase tracking-[0.22em] text-[#4A4A4A] hover:text-[#D92525] inline-flex items-center gap-2">
                <ChevronLeft size={14} /> होम पर वापस
              </Link>
              <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                <div>
                  <div className="eyebrow text-[#D92525] mb-3">आज का पेपर · Daily Edition</div>
                  <h1 className="font-serif-dev font-black text-[40px] md:text-[68px] leading-[0.98]">
                    ई-<span className="italic text-[#D92525]">पेपर</span>
                  </h1>
                  <p className="mt-4 font-sans-dev text-[15px] md:text-[16px] text-[#4A4A4A] max-w-[540px] leading-relaxed">
                    पूरी तस्वीर, एक क्लिक में। सुबह की चाय के साथ अख़बार को PDF या इमेज में पढ़ें, डाउनलोड करें, साझा करें।
                  </p>
                </div>
                {current && (
                  <div className="text-right">
                    <div className="text-[12px] uppercase tracking-[0.2em] text-[#4A4A4A]">वर्तमान संस्करण</div>
                    <div className="font-serif-dev font-black text-[22px] md:text-[26px] mt-1">{fmt(current.date)}</div>
                    <div className="text-[12px] text-[#4A4A4A] mt-1">{current.edition}</div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Body */}
          <section className="px-4 md:px-8 py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto">
              {list === null ? (
                <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#D92525]" size={36} /></div>
              ) : list.length === 0 ? (
                <ComingSoon />
              ) : (
                <div className="grid grid-cols-12 gap-8 md:gap-12">
                  {/* Viewer */}
                  <div className="col-span-12 lg:col-span-8">
                    {current ? (
                      <div className="border border-[#0F0F0F] bg-white p-4 md:p-6">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                          <div className="font-serif-dev font-bold text-[18px]">
                            {current.title || `ई-पेपर · ${fmt(current.date)}`}
                          </div>
                          {pdfFile && (
                            <a href={pdfFile.url} target="_blank" rel="noreferrer" download data-testid="epaper-download-pdf"
                              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] bg-[#0F0F0F] text-white px-4 py-2 rounded-full hover:bg-[#D92525] transition-colors">
                              <Download size={14} /> पूरा PDF डाउनलोड
                            </a>
                          )}
                        </div>

                        {pdfFile && !imageFiles.length ? (
                          <div className="w-full aspect-[3/4] md:aspect-[4/5] border border-[#0F0F0F]/20 bg-[#F4F4F0]" data-testid="epaper-pdf-viewer">
                            <iframe title="e-paper pdf" src={pdfFile.url} className="w-full h-full" style={{ border: 0 }} />
                          </div>
                        ) : imageFiles.length ? (
                          <>
                            <div className="w-full border border-[#0F0F0F]/20 bg-[#F4F4F0] flex items-center justify-center min-h-[420px]" data-testid="epaper-image-viewer">
                              <img src={imageFiles[page]?.url} alt={`Page ${page + 1}`} className="max-w-full max-h-[80vh] object-contain" />
                            </div>
                            {imageFiles.length > 1 && (
                              <div className="mt-4 flex items-center justify-between">
                                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} data-testid="epaper-prev"
                                  className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.18em] disabled:opacity-40">
                                  <ChevronLeft size={16} /> पिछला
                                </button>
                                <div className="text-[12px] text-[#4A4A4A]">पेज {page + 1} / {imageFiles.length}</div>
                                <button onClick={() => setPage(Math.min(imageFiles.length - 1, page + 1))} disabled={page === imageFiles.length - 1} data-testid="epaper-next"
                                  className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.18em] disabled:opacity-40">
                                  अगला <ChevronRight size={16} />
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="py-24 text-center text-[#4A4A4A]">इस संस्करण की फ़ाइल उपलब्ध नहीं है।</div>
                        )}
                      </div>
                    ) : (
                      <ComingSoon />
                    )}
                  </div>

                  {/* Archive list */}
                  <aside className="col-span-12 lg:col-span-4">
                    <div className="lg:sticky lg:top-32 border border-[#0F0F0F] bg-white">
                      <div className="px-5 py-4 border-b border-[#0F0F0F] flex items-center gap-2">
                        <Calendar size={16} className="text-[#D92525]" />
                        <span className="font-serif-dev font-bold text-[16px]">पुराने संस्करण</span>
                      </div>
                      <ul className="divide-y divide-[#0F0F0F]/10 max-h-[560px] overflow-y-auto">
                        {list.map((ed) => (
                          <li key={ed.id}>
                            <button
                              onClick={() => pickEdition(ed)}
                              data-testid={`epaper-edition-${ed.date}`}
                              className={`w-full text-left px-5 py-3.5 flex items-center justify-between gap-3 transition-colors ${current?.id === ed.id ? "bg-[#0F0F0F] text-white" : "hover:bg-[#F4F4F0]"}`}
                            >
                              <div>
                                <div className="font-serif-dev font-bold text-[15px]">{fmt(ed.date)}</div>
                                <div className={`text-[11px] uppercase tracking-[0.18em] ${current?.id === ed.id ? "text-white/60" : "text-[#4A4A4A]"}`}>
                                  {ed.edition} · {ed.files?.length || 0} फ़ाइल
                                </div>
                              </div>
                              <FileText size={14} className={current?.id === ed.id ? "text-white/60" : "text-[#4A4A4A]"} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </aside>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </SmoothScroll>
  );
}

function ComingSoon() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="epaper-coming-soon"
      className="max-w-[720px] mx-auto text-center py-16 md:py-28 border-y border-[#0F0F0F]"
    >
      <Newspaper size={64} className="mx-auto text-[#D92525] mb-6" />
      <div className="eyebrow text-[#D92525] mb-3">Coming Soon</div>
      <h2 className="font-serif-dev font-black text-[38px] md:text-[54px] leading-[1.02]">
        ई-पेपर <span className="italic">जल्द ही</span>
      </h2>
      <p className="mt-5 font-sans-dev text-[15px] md:text-[16px] text-[#4A4A4A] leading-relaxed max-w-[540px] mx-auto">
        देनवा पोस्ट का दैनिक ई-पेपर संस्करण जल्द ही यहाँ उपलब्ध होगा। हर सुबह पूरा अख़बार PDF और तस्वीर, दोनों में—एक क्लिक की दूरी पर।
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link to="/#subscribe" className="pill-btn text-[12px]">
          न्यूज़लेटर सब्सक्राइब करें
        </Link>
        <Link to="/" className="text-[12px] uppercase tracking-[0.22em] link-underline">
          होम पर वापस
        </Link>
      </div>
    </motion.div>
  );
}
